/**
 * API Error Classes for HeatCraftHP
 * Provides typed error handling with proper parsing of backend ErrorResponse
 */

/**
 * Base API Error class
 * Matches backend's ErrorResponse structure
 */
export class ApiError extends Error {
  constructor(statusCode, message, correlationId = null, detail = null, originalError = null) {
    super(message);
    this.name = 'ApiError';
    this.statusCode = statusCode;
    this.correlationId = correlationId;
    this.detail = detail;
    this.originalError = originalError;
    this.timestamp = new Date().toISOString();
  }

  /**
   * Parse error from Axios error response
   * @param {AxiosError} axiosError - The Axios error object
   * @returns {ApiError} Typed API error
   */
  static fromAxiosError(axiosError) {
    const response = axiosError.response;
    
    // Network error - no response received
    if (!response) {
      if (axiosError.code === 'ECONNABORTED') {
        return new NetworkError('Request timed out. Please check your connection and try again.');
      }
      if (axiosError.code === 'ERR_CANCELED') {
        return new RequestCancelledError('Request was cancelled.');
      }
      return new NetworkError('Unable to connect to the server. Please check your internet connection.');
    }

    const { status, data, headers } = response;
    const correlationId = headers?.['x-correlation-id'] || data?.correlationId || null;
    
    // Parse message from various response formats
    const message = ApiError.extractMessage(data, status);
    const detail = data?.detail || null;

    // Create appropriate error subclass based on status code
    switch (status) {
      case 400:
        return new ValidationError(message, correlationId, detail, data);
      case 401:
        return new UnauthorizedError(message, correlationId, detail);
      case 403:
        return new ForbiddenError(message, correlationId, detail);
      case 404:
        return new NotFoundError(message, correlationId, detail);
      case 409:
        return new ConflictError(message, correlationId, detail);
      case 422:
        return new UnprocessableEntityError(message, correlationId, detail, data);
      case 429:
        const retryAfter = data?.retryAfter || headers?.['retry-after'] || 60;
        return new RateLimitError(message, correlationId, retryAfter);
      case 500:
      case 502:
      case 503:
      case 504:
        return new ServerError(message, correlationId, detail, status);
      default:
        return new ApiError(status, message, correlationId, detail, axiosError);
    }
  }

  /**
   * Extract user-friendly message from response data
   */
  static extractMessage(data, statusCode) {
    if (!data) {
      return ApiError.getDefaultMessage(statusCode);
    }

    // Backend ErrorResponse format: { statusCode, message, correlationId, detail }
    if (data.message) {
      return data.message;
    }

    // Identity validation errors: { errors: { field: ["error1", "error2"] } }
    if (data.errors) {
      const errors = Object.values(data.errors).flat();
      if (errors.length > 0) {
        return errors.join('. ');
      }
    }

    // String response
    if (typeof data === 'string') {
      return data;
    }

    // Title from ProblemDetails
    if (data.title) {
      return data.title;
    }

    return ApiError.getDefaultMessage(statusCode);
  }

  /**
   * Get default message for status code
   */
  static getDefaultMessage(statusCode) {
    const messages = {
      400: 'The request was invalid. Please check your input and try again.',
      401: 'Please log in to continue.',
      403: 'You do not have permission to perform this action.',
      404: 'The requested resource was not found.',
      409: 'A conflict occurred. The resource may have been modified.',
      422: 'The request data could not be processed.',
      429: 'Too many requests. Please wait before trying again.',
      500: 'An unexpected error occurred. Please try again later.',
      502: 'The server is temporarily unavailable. Please try again later.',
      503: 'The service is temporarily unavailable. Please try again later.',
      504: 'The request timed out. Please try again later.',
    };
    return messages[statusCode] || 'An unexpected error occurred.';
  }

  /**
   * Check if this error should trigger a logout
   */
  get shouldLogout() {
    return this.statusCode === 401;
  }

  /**
   * Check if this error can be retried
   */
  get isRetryable() {
    return [408, 429, 500, 502, 503, 504].includes(this.statusCode);
  }

  /**
   * Convert to plain object for logging
   */
  toJSON() {
    return {
      name: this.name,
      statusCode: this.statusCode,
      message: this.message,
      correlationId: this.correlationId,
      detail: this.detail,
      timestamp: this.timestamp,
    };
  }
}

/**
 * Network error - no response received from server
 */
export class NetworkError extends ApiError {
  constructor(message = 'Network error occurred') {
    super(0, message);
    this.name = 'NetworkError';
  }

  get isRetryable() {
    return true;
  }
}

/**
 * Request was cancelled (e.g., component unmount)
 */
export class RequestCancelledError extends ApiError {
  constructor(message = 'Request was cancelled') {
    super(0, message);
    this.name = 'RequestCancelledError';
  }

  get isRetryable() {
    return false;
  }
}

/**
 * 400 Bad Request - Validation errors
 */
export class ValidationError extends ApiError {
  constructor(message, correlationId = null, detail = null, responseData = null) {
    super(400, message, correlationId, detail);
    this.name = 'ValidationError';
    this.validationErrors = this.parseValidationErrors(responseData);
  }

  parseValidationErrors(data) {
    if (!data?.errors) return {};
    
    const errors = {};
    for (const [field, messages] of Object.entries(data.errors)) {
      // Convert PascalCase to camelCase for field names
      const camelField = field.charAt(0).toLowerCase() + field.slice(1);
      errors[camelField] = Array.isArray(messages) ? messages : [messages];
    }
    return errors;
  }

  /**
   * Get error message for a specific field
   */
  getFieldError(fieldName) {
    return this.validationErrors[fieldName]?.[0] || null;
  }

  /**
   * Check if a specific field has errors
   */
  hasFieldError(fieldName) {
    return !!this.validationErrors[fieldName]?.length;
  }
}

/**
 * 401 Unauthorized - Not authenticated
 */
export class UnauthorizedError extends ApiError {
  constructor(message = 'Please log in to continue.', correlationId = null, detail = null) {
    super(401, message, correlationId, detail);
    this.name = 'UnauthorizedError';
  }
}

/**
 * 403 Forbidden - Authenticated but not authorized
 */
export class ForbiddenError extends ApiError {
  constructor(message = 'You do not have permission to perform this action.', correlationId = null, detail = null) {
    super(403, message, correlationId, detail);
    this.name = 'ForbiddenError';
  }
}

/**
 * 404 Not Found
 */
export class NotFoundError extends ApiError {
  constructor(message = 'The requested resource was not found.', correlationId = null, detail = null) {
    super(404, message, correlationId, detail);
    this.name = 'NotFoundError';
  }
}

/**
 * 409 Conflict
 */
export class ConflictError extends ApiError {
  constructor(message = 'A conflict occurred.', correlationId = null, detail = null) {
    super(409, message, correlationId, detail);
    this.name = 'ConflictError';
  }
}

/**
 * 422 Unprocessable Entity
 */
export class UnprocessableEntityError extends ValidationError {
  constructor(message, correlationId = null, detail = null, responseData = null) {
    super(message, correlationId, detail, responseData);
    this.statusCode = 422;
    this.name = 'UnprocessableEntityError';
  }
}

/**
 * 429 Rate Limited
 */
export class RateLimitError extends ApiError {
  constructor(message = 'Too many requests. Please try again later.', correlationId = null, retryAfter = 60) {
    super(429, message, correlationId);
    this.name = 'RateLimitError';
    this.retryAfter = retryAfter; // seconds
  }

  get isRetryable() {
    return true;
  }
}

/**
 * 5xx Server Error
 */
export class ServerError extends ApiError {
  constructor(message = 'An unexpected error occurred. Please try again later.', correlationId = null, detail = null, statusCode = 500) {
    super(statusCode, message, correlationId, detail);
    this.name = 'ServerError';
  }
}

/**
 * Helper to check if an error is an API error
 */
export function isApiError(error) {
  return error instanceof ApiError;
}

/**
 * Helper to check if an error is a validation error
 */
export function isValidationError(error) {
  return error instanceof ValidationError;
}

/**
 * Helper to check if user should be logged out
 */
export function shouldLogout(error) {
  return error instanceof ApiError && error.shouldLogout;
}

const apiErrorUtilities = {
  ApiError,
  NetworkError,
  RequestCancelledError,
  ValidationError,
  UnauthorizedError,
  ForbiddenError,
  NotFoundError,
  ConflictError,
  UnprocessableEntityError,
  RateLimitError,
  ServerError,
  isApiError,
  isValidationError,
  shouldLogout,
};

export default apiErrorUtilities;
